import React from 'react';
import {Dialog} from 'primereact/dialog';
import {Card} from 'primereact/card';
import {Badge} from 'primereact/badge';
import {Button} from 'primereact/button';

// Función helper para formatear fechas
const formatDate = (dateString) => {
    try {
        if (!dateString) return 'Fecha no disponible';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Fecha inválida';
        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    } catch (error) {
        return 'Fecha no disponible';
    }
};

// Función helper para obtener el año de una fecha
const getYear = (dateString) => {
    try {
        if (!dateString) return '-';
        const year = new Date(dateString).getFullYear();
        return isNaN(year) ? '-' : year;
    } catch (error) {
        return '-';
    }
};

// Función helper para determinar el tipo de archivo
const getFileType = (filename) => {
    if (!filename || typeof filename !== 'string') return 'unknown';
    const parts = filename.split('.');
    return parts.length > 1 ? parts.pop().toLowerCase() : 'unknown';
};

// Función helper para obtener el icono según el tipo de archivo
const getFileIcon = (filename) => {
    const extension = getFileType(filename);
    switch (extension) {
        case 'pdf':
            return 'pi pi-file-pdf';
        case 'doc':
        case 'docx':
            return 'pi pi-file-word';
        case 'xls':
        case 'xlsx':
            return 'pi pi-file-excel';
        case 'jpg':
        case 'jpeg':
        case 'png':
        case 'gif':
            return 'pi pi-image';
        case 'zip':
        case 'rar':
            return 'pi pi-file-archive';
        default:
            return 'pi pi-file';
    }
};

// Componente principal del diálogo
const ReferencesDialog = ({
                              selectedDocument,
                              showReferences,
                              setShowReferences,
                              onFileOpen // Función callback para abrir archivos
                          }) => {

    // Validación más robusta de referencias
    const getValidReferences = () => {
        if (!selectedDocument) return [];
        if (!selectedDocument.referencias) return [];
        if (!Array.isArray(selectedDocument.referencias)) return [];

        // Filtrar referencias válidas (que tengan al menos archivo y fecha)
        return selectedDocument.referencias.filter(ref =>
            ref &&
            typeof ref === 'object' &&
            ref.archivo &&
            ref.fecha
        );
    };

    // Ordenar referencias por fecha (más reciente primero) con validación
    const sortedReferences = getValidReferences().sort((a, b) => {
        try {
            const dateA = new Date(a.fecha);
            const dateB = new Date(b.fecha);

            // Si alguna fecha es inválida, ponerla al final
            if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
            if (isNaN(dateA.getTime())) return 1;
            if (isNaN(dateB.getTime())) return -1;

            return dateB - dateA;
        } catch (error) {
            return 0;
        }
    });

    const handleFileOpen = (reference) => {
        try {
            if (onFileOpen && reference) {
                onFileOpen(reference);
            }
        } catch (error) {
            console.error('Error al abrir archivo:', error);
        }
    };

    // Función para obtener el período de fechas de forma segura
    const getPeriod = () => {
        if (sortedReferences.length === 0) return '-';
        try {
            const firstYear = getYear(sortedReferences[sortedReferences.length - 1].fecha);
            const lastYear = getYear(sortedReferences[0].fecha);
            return firstYear === lastYear ? firstYear : `${firstYear} - ${lastYear}`;
        } catch (error) {
            return '-';
        }
    };

    return (
        <Dialog
            header={
                <div className="dialog-header-custom">
                    <i className="pi pi-folder-open mr-2"></i>
                    <span>Referencias - {selectedDocument?.documento || 'Documento sin nombre'}</span>
                </div>
            }
            visible={showReferences}
            onHide={() => setShowReferences(false)}
            style={{ width: '70vw', maxWidth: '1000px' }}
            maximizable
            className="references-dialog-container"
        >
            {/* Validación principal: si no hay documento seleccionado */}
            {!selectedDocument ? (
                <div className="no-document">
                    <i className="pi pi-exclamation-triangle"></i>
                    <h3>Documento no seleccionado</h3>
                    <p>No se ha seleccionado ningún documento para mostrar las referencias.</p>
                </div>
            ) : (
                <div className="references-content">
                    {/* Estadísticas rápidas - Solo si hay referencias */}
                    {sortedReferences.length > 0 && (
                        <div className="references-stats">
                            <div className="stat-item">
                                <i className="pi pi-file"></i>
                                <div>
                                    <span className="stat-label">Archivos</span>
                                    <span className="stat-number">{sortedReferences.length}</span>
                                </div>
                            </div>
                            <div className="stat-item">
                                <i className="pi pi-calendar"></i>
                                <div>
                                    <span className="stat-label">Período</span>
                                    <span className="stat-number">{getPeriod()}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Grid de tarjetas de archivos */}
                    {sortedReferences.length > 0 && (
                        <div className="references-grid">
                            {sortedReferences.map((reference, index) => (
                                <Card
                                    key={`${reference.archivo}-${index}`}
                                    className={`file-reference-card ${reference?.renovado ? 'renovado' : ''}`}
                                >
                                    <div className="file-card-content">
                                        {/* Header de la tarjeta */}
                                        <div className="file-card-header">
                                            <div className="file-icon-container">
                                                <i className={`${getFileIcon(reference.archivo)} file-icon`}></i>
                                            </div>
                                            <div className="file-badges">
                                                {reference.renovado && (
                                                    <Badge
                                                        value="Renovado"
                                                        severity="success"
                                                        className="renovado-badge"
                                                    />
                                                )}
                                                <Badge
                                                    value={getFileType(reference.archivo).toUpperCase()}
                                                    severity="info"
                                                    className="file-type-badge"
                                                />
                                            </div>
                                        </div>

                                        {/* Información del archivo */}
                                        <div className="file-info">
                                            <h4 className="file-name" title={reference.archivo || 'Archivo sin nombre'}>
                                                {reference.archivo || 'Archivo sin nombre'}
                                            </h4>
                                            <div className="file-meta">
                                                <div className="meta-item">
                                                    <i className="pi pi-calendar-plus"></i>
                                                    <span>{formatDate(reference.fecha)}</span>
                                                </div>
                                                <div className="meta-item">
                                                    <i className="pi pi-clock"></i>
                                                    <span>Año {getYear(reference.fecha)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Acciones */}
                                        <div className="file-actions">
                                            <Button
                                                icon="pi pi-eye"
                                                label="Ver"
                                                className="p-button-sm p-button-outlined"
                                                onClick={() => handleFileOpen(reference)}
                                                tooltip="Abrir archivo"
                                                tooltipOptions={{position: 'top'}}
                                                disabled={!reference.archivo}
                                            />
                                            {/*<Button
                                                icon="pi pi-download"
                                                className="p-button-sm p-button-text"
                                                onClick={() => handleFileOpen(reference)}
                                                tooltip="Descargar archivo"
                                                tooltipOptions={{position: 'top'}}
                                                disabled={!reference.archivo}
                                            />*/}
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}

                    {/* Mensaje si no hay referencias válidas */}
                    {sortedReferences.length === 0 && (
                        <div className="no-references">
                            <i className="pi pi-info-circle"></i>
                            <h3>No hay referencias disponibles</h3>
                            <p>
                                {!selectedDocument.referencias
                                    ? 'Este documento no tiene archivos de referencia asociados.'
                                    : 'No se encontraron referencias válidas para este documento.'}
                            </p>
                        </div>
                    )}
                </div>
            )}
        </Dialog>
    );
};

export default ReferencesDialog;